import { GooglePlaceAutocompleteType } from '@sortlist-frontend/shared-components';
import { TFunction } from '@sortlist-frontend/translation/ssr';
import { isPhoneNumber } from '@sortlist-frontend/utils';
import { array, number, object, SchemaOf, string } from 'yup';

const MAX_BUDGET = 1000000;
export type Language = {
  value: string;
  label: string;
};

export type AgencyClaimFormValues = {
  agencyName: string;
  agencyUuid?: string;
  email: string;
  teamSize: number;
  languages: Language[];
  phone: string;
  website: string;
  officeAddress: GooglePlaceAutocompleteType;
  services: {
    skill_tags: {
      name: string;
      uuid?: string;
    }[];
    description: string;
    expertise_id: number;
    position: number;
    budget: number[];
  }[];
};

export const buildValidationSchema = (t: TFunction): SchemaOf<AgencyClaimFormValues> => {
  const fieldIsRequired = t('common:errors.fieldIsRequired');
  const fieldIsPositiveInteger = t('common:errors.positiveIntegerField');
  const validOfficeAddres = t('common:errors.pleaseEnterValidOffice');
  return object().shape({
    agencyUuid: string().notRequired(),
    agencyName: string().required(t('common:errors.agencyNameRequired')),
    officeAddress: object()
      .shape({
        address: string().required(validOfficeAddres),
        coordinates: object()
          .shape({
            type: string().required(validOfficeAddres),
            coordinates: array().of(number().required()).min(2).max(2).required(validOfficeAddres),
          })
          .required(validOfficeAddres),
        google_place_id: string().required(t('common:errors.pleaseEnterValidOfficeAgain')),
        language: string().required(validOfficeAddres),
      })
      .required(validOfficeAddres),
    languages: array()
      .required()
      .min(1, t('common:errors.pleaseSelectLanguage'))
      .of(object().shape({ value: string().required(), label: string().required() }).required()),
    teamSize: number()
      .typeError(fieldIsRequired)
      .integer(fieldIsPositiveInteger)
      .positive(fieldIsPositiveInteger)
      .required(fieldIsRequired),
    phone: string()
      .test('validatePhoneNumber', t('common:errors.invalidPhoneNumber'), (valueToValidate) => {
        return isPhoneNumber(valueToValidate as string);
      })
      .required(fieldIsRequired),
    website: string()
      .transform((value, originalValue) => {
        if (!originalValue) return '';
        if (originalValue.startsWith('https://')) return originalValue;
        if (originalValue.startsWith('http://')) return originalValue;
        return `https://${originalValue}`;
      })
      .url(t('common:errors.pleaseEnterValidWebsite'))
      .required(fieldIsRequired),
    email: string().email(t('common:errors.pleaseEnterValidEmail')).required(fieldIsRequired),
    services: array()
      .of(
        object().shape({
          skill_tags: array().of(object().shape({ name: string().required(), uuid: string().optional() })),
          description: string().default('').defined(),
          expertise_id: number().defined(),
          position: number().defined(),
          budget: array()
            .of(number().required())
            .required(fieldIsRequired)
            .min(1)
            .max(2)
            .transform((value, originalValue) => {
              if (originalValue.length == 1) originalValue.push(MAX_BUDGET);
              return originalValue;
            }),
        }),
      )
      .required(t('common:errors.pleaseSelectService'))
      .min(1, t('common:errors.pleaseSelectService')),
  });
};
